import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Chip,
  Divider,
  Card,
  RadioGroup,
  Radio,
  Switch,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as CouponIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Description,
  Info as InfoIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  addCoupon,
  deleteCoupon,
  editCoupon,
  fetchAllCoupons,
  fetchAllCouponUsers
} from "../../features/users/userSlice";
import {
  selectCouponUsers,
  selectUsersCoupons
} from "../../features/users/userSelectors";
import useSignedUrl from "../../utils/useSignedUrl";
import getInitials from "../../utils/getInitials";
import PurchaseButtons from "./AdvancePurchaseButtons";
const ImageItem = ({ img }) => {
  const imgSrc = useSignedUrl(
    img,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  return (
    <Box
      component="img"
      borderRadius="50%"
      height={40}
      width={40}
      src={imgSrc}
      alt="avatar"
    />
  );
};
const CouponManagement = () => {
  const coupons = useSelector(selectUsersCoupons);
  const [open, setOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [affectedUsers, setAffectedUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [formData, setFormData] = useState({
    couponCode: "",
    amount: "",
    waivingLogging: "0",
    description: "",
    interval: "yearly",
    trainingAccess: {
      level1: false,
      level2: false,
      level3: false
    }
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [currentTab, setCurrentTab] = useState("recent");
  const dispatch = useDispatch();

  useEffect(() => {
    fetchCoupons();
  }, [dispatch, currentTab]);

  const fetchCoupons = () => {
    dispatch(fetchAllCoupons(currentTab));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    resetForm();
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };
  const handleIntervalChange = (e) => {
    const intervalValue = e.target.value;
    // Reset waiving logging to 0 when interval changes
    setFormData((prev) => ({
      ...prev,
      interval: intervalValue,
      waivingLogging: "0"
    }));
  };
  const resetForm = () => {
    setFormData({
      couponCode: "",
      amount: "",
      waivingLogging: "",
      description: "",
      interval: "yearly",
      trainingAccess: {
        level1: false,
        level2: false,
        level3: false
      }
    });
    setEditMode(false);
    setEditId(null);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "waivingLogging") {
      const interval =
        value === "30"
          ? "monthly"
          : // : value === "90" || value === "365"
            // ? "yearly"
            formData.interval;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        interval
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCheckboxChange = (level) => {
    setFormData((prev) => ({
      ...prev,
      trainingAccess: {
        ...prev.trainingAccess,
        [level]: !prev.trainingAccess[level]
      }
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      if (formData.waivingLogging == 90) {
        formData.first_quarter_free = true;
      } else {
        formData.first_quarter_free = false;
      }
      if (formData.waivingLogging == 365 && formData.interval === "monthly") {
        formData.first_year_free_monthly = true;
      } else {
        formData.first_year_free_monthly = false;
      }
      const { payload } = !editId
        ? await dispatch(addCoupon(formData))
        : await dispatch(editCoupon({ ...formData, id: editId }));

      if (!payload?.couponCode) throw new Error(payload?.message);

      showSnackbar(`Coupon ${editMode ? "updated" : "created"} successfully`);
      handleClose();
      fetchCoupons();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (coupon) => {
    setFormData({
      couponCode: coupon.couponCode,
      amount: coupon.amount,
      waivingLogging: coupon.waivingLogging,
      interval: coupon.interval || "yearly", // Add this line
      trainingAccess: coupon.trainingAccess,
      description: coupon.description || ""
    });
    setEditMode(true);
    setEditId(coupon._id);
    setOpen(true);
  };

  const handleDeleteClick = async (id) => {
    if (currentTab === "archive") {
      try {
        // Fetch affected users for this coupon
        const response = await dispatch(fetchAllCouponUsers(id));
        if (response.payload?.appliedUsers?.length) {
          setAffectedUsers(response.payload.appliedUsers);
          setDeleteItemId(id);
          setConfirmDeleteOpen(true);
        } else {
          // If no affected users, proceed with deletion
          executeDelete(id, currentTab);
        }
      } catch (error) {
        showSnackbar(error.message, "error");
      }
    } else {
      // For recent tab, delete without confirmation
      executeDelete(id, currentTab);
    }
  };

  const executeDelete = async (id, tab) => {
    try {
      await dispatch(deleteCoupon({ id, tab }));
      showSnackbar(
        `Coupon ${tab === "recent" ? "archived" : "deleted"} successfully`
      );
      setConfirmDeleteOpen(false);
      fetchCoupons();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const handleConfirmDelete = () => {
    if (deleteItemId) {
      executeDelete(deleteItemId, currentTab);
    }
  };

  const getWaivingLoggingOptions = (interval) => {
    return [
      { value: "0", label: "0 days" },
      { value: "90", label: "90 days" },
      { value: "365", label: "365 days" }
    ];
  };

  return (
    <Box>
      <PurchaseButtons />
      <Card sx={{ mb: 4, p: 2, boxShadow: "none" }}>
        <Box sx={{ display: "flex", alignItems: "end", mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              flexGrow: 1,
              "& .MuiTab-root": {
                textTransform: "none"
              }
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="recent" label="Recent" />
            <Tab value="archive" label="Archived" />
          </Tabs>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{
              ml: "auto",
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1
            }}
          >
            Add New Coupon
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />

        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CouponIcon sx={{ mr: 1 }} /> Coupon Code
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Description sx={{ mr: 1 }} /> Description
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <MoneyIcon sx={{ mr: 1 }} /> Amount
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TimeIcon sx={{ mr: 1 }} /> Waiving Logging
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Training Access
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Interval
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  {currentTab === "recent" ? "Refer Friend" : "Restore"}
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons?.length ? (
                coupons.map((coupon) => (
                  <TableRow
                    key={coupon._id}
                    sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                  >
                    <TableCell>
                      <Chip
                        label={coupon.couponCode}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {coupon.description || "No description"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="success.main" fontWeight="bold">
                        ${coupon.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<TimeIcon />}
                        label={`${coupon.waivingLogging} days`}
                        color="secondary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {Object.entries(coupon.trainingAccess)
                          .filter(([_, value]) => value)
                          .map(([key]) => (
                            <Chip
                              key={key}
                              label={key.replace("level", "Pillar ")}
                              size="small"
                              color="info"
                            />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          coupon.interval?.charAt(0).toUpperCase() +
                          coupon.interval?.slice(1)
                        }
                        color="warning"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={
                          currentTab === "recent"
                            ? coupon?.is_refer_friend
                            : coupon?.isActive
                        }
                        onChange={async (e) => {
                          await dispatch(
                            editCoupon({
                              ...(currentTab === "recent"
                                ? { is_refer_friend: e.target.value }
                                : { isActive: true }),
                              id: coupon?._id
                            })
                          );
                          fetchCoupons();
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(coupon)}
                        color="primary"
                        sx={{ "&:hover": { backgroundColor: "primary.light" } }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(coupon._id)}
                        color="error"
                        sx={{ "&:hover": { backgroundColor: "error.light" } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No coupons found in{" "}
                      {currentTab === "recent" ? "recent" : "archive"}.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Coupon Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <CouponIcon />
          {editMode ? "Edit Coupon" : "Add New Coupon"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3
            }}
          >
            <TextField
              name="couponCode"
              label="Coupon Code"
              value={formData.couponCode}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <CouponIcon sx={{ mr: 1, color: "primary.main" }} />
                )
              }}
            />
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <Description
                    sx={{
                      mr: 1,
                      color: "primary.main"
                    }}
                  />
                )
              }}
            />
            <TextField
              name="amount"
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <MoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                )
              }}
            />
            <TextField
              select
              name="waivingLogging"
              label="Waiving Logging (Days)"
              value={formData.waivingLogging}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <TimeIcon sx={{ mr: 1, color: "primary.main" }} />
                )
              }}
              SelectProps={{
                native: true
              }}
            >
              {getWaivingLoggingOptions(formData.interval).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Billing Interval
              </Typography>
              <RadioGroup
                value={formData.interval}
                onChange={handleIntervalChange}
              >
                <FormControlLabel
                  value="monthly"
                  control={<Radio color="primary" />}
                  label="Monthly"
                />
                <FormControlLabel
                  value="yearly"
                  control={<Radio color="primary" />}
                  label="Yearly"
                />
              </RadioGroup>
            </Card>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Training Access Levels
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.trainingAccess.level1}
                      onChange={() => handleCheckboxChange("level1")}
                      color="primary"
                    />
                  }
                  label="Pillar 1"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.trainingAccess.level2}
                      onChange={() => handleCheckboxChange("level2")}
                      color="primary"
                    />
                  }
                  label="Pillar 2"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.trainingAccess.level3}
                      onChange={() => handleCheckboxChange("level3")}
                      color="primary"
                    />
                  }
                  label="Pillar 3"
                />
              </FormGroup>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {editMode ? "Update Coupon" : "Create Coupon"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog for Archive Tab */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "error.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <InfoIcon />
          Warning: Deletion Will Affect Users
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" pt={2} mb={0}>
            Deleting this coupon will affect the following users:
          </Typography>
          {affectedUsers.length > 0 ? (
            <List sx={{ bgcolor: "background.paper", borderRadius: 1, mb: 2 }}>
              {affectedUsers.map((user, index) => (
                <ListItem
                  key={index}
                  divider={index < affectedUsers.length - 1}
                >
                  <ListItemAvatar>
                    {user?.profilePic ? (
                      <ImageItem img={user?.profilePic} />
                    ) : (
                      <Avatar sx={{ width: 40, height: 40 }}>
                        {user.fullName
                          ? getInitials(user.fullName)
                          : getInitials(user.displayName)}
                      </Avatar>
                    )}
                    {/* <Avatar src={user?.profilePic}>
                      {(user?.displayName ||
                        user?.fullName ||
                        "U")[0].toUpperCase()}
                    </Avatar> */}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      user?.fullName || user?.displayName || "Unknown User"
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">
              Loading affected users...
            </Typography>
          )}
          <Typography variant="body1" color="error">
            Are you sure you want to proceed with deletion?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Delete Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.message?.includes("success") ? "success" : "error"}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          elevation={6}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponManagement;
